import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { ArrowForward, CheckCircle } from '@mui/icons-material';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow, parseISO } from 'date-fns';
import api from '../config/api';
import { computeIntroductionFeeAmount, introductionFeeCopyVars } from '../config/plans';
import usePartnerEntitlements from '../hooks/usePartnerEntitlements';
import NumericTextField from '../components/NumericTextField';
import {
  PartnerEmptyState,
  PartnerFilterBar,
  PartnerLoading,
  PartnerPageHeader,
  PartnerPageShell,
  partnerLinkSx,
  partnerOxbloodBtnSx,
} from '../components/PartnerListChrome';
import { DOCUMENT, documentEyebrowSx } from '../constants/documentTheme';
import {
  ALERT,
  averageReplyHours,
  formatMoney,
  getInquiryBucket,
  getInquiryKind,
  getInquiryDisplaySubtitle,
  getInquiryDisplayTitle,
  inquiryNeedsReply,
  filterVisibleInquiryMessages,
  matchesQuery,
} from '../utils/partnerListUtils';
import BookForClientDialog from '../components/BookForClientDialog';
import InquirySubjectTag from '../components/InquirySubjectTag';
import PartnerBookingDetailsDialog from '../components/PartnerBookingDetailsDialog';
import PartnerInquiryMessageThread from '../components/PartnerInquiryMessageThread';
import { getPartnerSessionFromToken } from '../utils/partnerPush';
import { getPartnerTripPath } from '../utils/partnerTripPaths';

const PartnerInquiries = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { inquiryId } = useParams();
  const partnerSession = useMemo(() => getPartnerSessionFromToken(), []);

  const [inquiries, setInquiries] = useState([]);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);

  const [markAsBookedOpen, setMarkAsBookedOpen] = useState(false);
  const { introductionFeeRate } = usePartnerEntitlements();
  const inquiryIsOwnClient = selectedInquiry?.attribution === 'partner_client';
  const bookedFeeRate = inquiryIsOwnClient ? 0 : (introductionFeeRate ?? 0);
  const [bookingAmount, setBookingAmount] = useState('');
  const [dmcNet, setDmcNet] = useState('');
  const [clientPrice, setClientPrice] = useState('');
  const [savingQuote, setSavingQuote] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [travelDate, setTravelDate] = useState('');
  const [bookingNotes, setBookingNotes] = useState('');
  const [markingAsBooked, setMarkingAsBooked] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [bookForClientOpen, setBookForClientOpen] = useState(false);
  const [detailsBooking, setDetailsBooking] = useState(null);
  const [detailsModal, setDetailsModal] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamMemberFilter, setTeamMemberFilter] = useState(
    partnerSession.isPartnerUser ? 'mine' : 'all'
  );

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('partnerToken');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const openBookingDetails = useCallback(async (bookingId) => {
    if (!bookingId) return;
    try {
      setDetailsLoading(true);
      setError(null);
      const response = await api.get(`/package-booking/partner/bookings/${bookingId}`, {
        headers: getAuthHeaders(),
      });
      if (response.data?.booking) {
        setDetailsBooking(response.data.booking);
        setDetailsModal(true);
      } else {
        setError(t('partnerLists.loadBookingsFailed'));
      }
    } catch (err) {
      console.error('Failed to load booking details:', err);
      setError(t('partnerLists.loadBookingsFailed'));
    } finally {
      setDetailsLoading(false);
    }
  }, [getAuthHeaders, t]);

  const closeBookingDetails = () => {
    setDetailsModal(false);
    setDetailsBooking(null);
  };

  const loadInquiryDetails = useCallback(async (id) => {
    try {
      setMessagesLoading(true);
      const response = await api.get(`/package-booking/partner/inquiries/${id}`, {
        headers: getAuthHeaders()
      });

      if (response.data.success) {
        setSelectedInquiry(response.data.inquiry);
        setMessages(response.data.messages);
      }
    } catch (err) {
      console.error('Failed to load inquiry details:', err);
    } finally {
      setMessagesLoading(false);
    }
  }, [getAuthHeaders]);

  const loadInquiries = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: '200', includeArchived: 'all' });
      if (teamMemberFilter === 'mine') {
        params.set('requestedFilter', 'mine');
      } else if (teamMemberFilter === 'anyone') {
        params.set('requestedFilter', 'anyone');
      } else if (teamMemberFilter !== 'all') {
        params.set('requestedPartnerUserId', teamMemberFilter);
      }
      const response = await api.get(`/package-booking/partner/inquiries?${params.toString()}`, {
        headers: getAuthHeaders()
      });

      if (response.data.success) {
        setInquiries(response.data.inquiries || []);

        if (inquiryId && response.data.inquiries.length > 0) {
          const found = response.data.inquiries.find(i => i.id === parseInt(inquiryId, 10));
          if (found) {
            loadInquiryDetails(found.id);
          }
        }
      }
    } catch (err) {
      console.error('Failed to load inquiries:', err);
      setError(t('partnerLists.loadInquiriesFailed'));
    } finally {
      setLoading(false);
    }
  }, [inquiryId, getAuthHeaders, loadInquiryDetails, t, teamMemberFilter]);

  const loadTeamMembers = useCallback(async () => {
    try {
      const response = await api.get('/partners/team-members', {
        headers: getAuthHeaders(),
      });
      if (response.data?.users) {
        setTeamMembers(response.data.users);
      }
    } catch (err) {
      console.error('Failed to load team members:', err);
    }
  }, [getAuthHeaders]);

  const sendReply = async () => {
    if (!replyMessage.trim() || !selectedInquiry) return;

    try {
      setSending(true);
      await api.post(
        `/package-booking/partner/inquiries/${selectedInquiry.id}/message`,
        { message: replyMessage },
        { headers: getAuthHeaders() }
      );

      setMessages(prev => [...prev, {
        id: Date.now(),
        sender_type: 'partner',
        sender_name: 'You',
        message: replyMessage,
        created_at: new Date().toISOString()
      }]);

      setReplyMessage('');

      setInquiries(prev => prev.map(i =>
        i.id === selectedInquiry.id
          ? { ...i, status: 'responded', partner_responded: true, unread_count: 0 }
          : i
      ));
      setSelectedInquiry((prev) => prev ? { ...prev, status: 'responded', partner_responded: true } : prev);
    } catch (err) {
      console.error('Failed to send reply:', err);
      setError(t('partnerLists.sendFailed'));
    } finally {
      setSending(false);
    }
  };

  const handleCreateProposal = async () => {
    if (!selectedInquiry) return;
    try {
      setSending(true);
      setError(null);
      const response = await api.post(
        `/package-booking/partner/inquiries/${selectedInquiry.id}/create-proposal`,
        {},
        { headers: getAuthHeaders() }
      );
      setSelectedInquiry((prev) => prev ? {
        ...prev,
        status: 'quoted',
        trip_id: response.data.tripId,
        linked_trip_id: response.data.tripId,
        linked_trip_is_master: false,
      } : prev);
      setSuccessMessage(t('partnerLists.proposalCreated'));
      if (response.data?.tripId) {
        navigate(`/partner/trips/${response.data.tripId}`);
      }
    } catch (err) {
      setError(err.response?.data?.error || t('partnerLists.proposalFailed'));
    } finally {
      setSending(false);
    }
  };

  const handleCloseInquiry = async () => {
    if (!selectedInquiry) return;
    try {
      setSending(true);
      setError(null);
      await api.patch(
        `/package-booking/partner/inquiries/${selectedInquiry.id}`,
        { status: 'closed' },
        { headers: getAuthHeaders() }
      );
      setSelectedInquiry((prev) => prev ? { ...prev, status: 'closed' } : prev);
      setInquiries((prev) => prev.map((item) => (
        item.id === selectedInquiry.id ? { ...item, status: 'closed' } : item
      )));
      setSuccessMessage(t('partnerLists.inquiryClosed'));
    } catch (err) {
      setError(err.response?.data?.error || t('partnerLists.closeFailed'));
    } finally {
      setSending(false);
    }
  };

  const handleArchiveInquiry = async (archived = true) => {
    if (!selectedInquiry) return;
    try {
      setSending(true);
      setError(null);
      await api.patch(
        `/package-booking/partner/inquiries/${selectedInquiry.id}/archive`,
        { archived },
        { headers: getAuthHeaders() }
      );
      setInquiries((prev) => prev.map((item) => (
        item.id === selectedInquiry.id ? { ...item, partner_archived: archived } : item
      )));
      setSelectedInquiry((prev) => (prev ? { ...prev, partner_archived: archived } : prev));
      setSuccessMessage(
        archived ? t('partnerLists.inquiryArchived') : t('partnerLists.inquiryRestored')
      );
      if (archived && activeTab !== 'archived') {
        setSelectedInquiry(null);
        navigate('/partner/inquiries');
      }
    } catch (err) {
      setError(
        err.response?.data?.error
        || (archived ? t('partnerLists.archiveInquiryFailed') : t('partnerLists.restoreInquiryFailed'))
      );
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (!selectedInquiry) return;
    setDmcNet(selectedInquiry.dmc_net_amount == null ? '' : String(selectedInquiry.dmc_net_amount));
    setClientPrice(selectedInquiry.quoted_price == null ? '' : String(selectedInquiry.quoted_price));
  }, [selectedInquiry]);

  const handleSaveQuote = async () => {
    if (!selectedInquiry) return;
    try {
      setSavingQuote(true);
      setError(null);
      const response = await api.patch(
        `/package-booking/partner/inquiries/${selectedInquiry.id}`,
        {
          dmc_net_amount: dmcNet === '' ? null : Number(dmcNet),
          quoted_price: clientPrice === '' ? null : Number(clientPrice),
        },
        { headers: getAuthHeaders() }
      );
      const saved = response.data || {};
      setSelectedInquiry((prev) => (prev ? {
        ...prev,
        dmc_net_amount: saved.dmc_net_amount,
        quoted_price: saved.quoted_price,
      } : prev));
      setSuccessMessage(t('partnerLists.quoteSaved'));
    } catch (err) {
      setError(err.response?.data?.error || t('partnerLists.quoteSaveFailed'));
    } finally {
      setSavingQuote(false);
    }
  };

  const openMarkAsBookedDialog = () => {
    if (selectedInquiry) {
      setCustomerName(selectedInquiry.user_name || '');
      setBookingAmount(
        selectedInquiry.quoted_price
        || selectedInquiry.selected_rate_price
        || selectedInquiry.base_price
        || ''
      );
      setTravelDate(selectedInquiry.preferred_dates ? selectedInquiry.preferred_dates.split(' - ')[0] : '');
      setBookingNotes('');
      setMarkAsBookedOpen(true);
    }
  };

  const handleMarkAsBooked = async () => {
    if (!selectedInquiry || !bookingAmount) return;

    try {
      setMarkingAsBooked(true);
      const response = await api.post(
        `/partner-commission/inquiries/${selectedInquiry.id}/mark-as-booked`,
        {
          bookingAmount: parseFloat(bookingAmount),
          customerName,
          travelDate: travelDate || null,
          notes: bookingNotes
        },
        { headers: getAuthHeaders() }
      );

      if (response.data.success) {
        setInquiries(prev => prev.map(i =>
          i.id === selectedInquiry.id
            ? { ...i, status: 'converted', converted_to_sale: true }
            : i
        ));
        setSelectedInquiry(prev => ({ ...prev, status: 'converted', converted_to_sale: true }));
        setSuccessMessage(t('partnerLists.bookingRecorded', {
          amount: response.data.data.commissionAmount.toFixed(2),
          rate: response.data.data.commissionRate,
        }));
        setMarkAsBookedOpen(false);
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } catch (err) {
      console.error('Failed to mark as booked:', err);
      setError(err.response?.data?.error || t('partnerLists.recordFailed'));
    } finally {
      setMarkingAsBooked(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem('partnerToken')) {
      navigate('/partner/login');
      return;
    }
    loadInquiries();
    loadTeamMembers();
  }, [loadInquiries, loadTeamMembers, navigate]);

  const formatTime = (dateString) => {
    if (!dateString) return '';
    try {
      return formatDistanceToNow(parseISO(String(dateString).replace(' ', 'T')), { addSuffix: false });
    } catch (err) {
      return '';
    }
  };

  const searched = useMemo(() => inquiries.filter((inquiry) => matchesQuery([
    inquiry.user_name,
    inquiry.user_email,
    inquiry.package_title,
    inquiry.last_message,
    inquiry.message,
    inquiry.booking_reference,
  ], search)), [inquiries, search]);

  const activeSearched = useMemo(
    () => searched.filter((inquiry) => getInquiryBucket(inquiry) !== 'archived'),
    [searched]
  );

  const visibleInquiries = useMemo(() => {
    if (activeTab === 'all') return activeSearched;
    return searched.filter((inquiry) => getInquiryBucket(inquiry) === activeTab);
  }, [searched, activeSearched, activeTab]);

  const counts = {
    all: activeSearched.length,
    needsReply: activeSearched.filter((inquiry) => getInquiryBucket(inquiry) === 'needsReply').length,
    answered: activeSearched.filter((inquiry) => getInquiryBucket(inquiry) === 'answered').length,
    archived: searched.filter((inquiry) => getInquiryBucket(inquiry) === 'archived').length,
  };

  const avgHours = averageReplyHours(inquiries);
  const inquiryConverted = selectedInquiry?.status === 'converted'
    || selectedInquiry?.status === 'quoted'
    || Boolean(selectedInquiry?.trip_id)
    || Boolean(selectedInquiry?.converted_to_sale);
  const convertedTripPath = getPartnerTripPath({
    id: selectedInquiry?.linked_trip_id,
    is_master_trip: Boolean(selectedInquiry?.linked_trip_is_master),
  });
  const inquiryClosed = selectedInquiry?.status === 'closed';
  const visibleMessages = useMemo(
    () => filterVisibleInquiryMessages(
      messages,
      selectedInquiry,
      t('partnerLists.customRequest')
    ),
    [messages, selectedInquiry, t]
  );

  useEffect(() => {
    if (!selectedInquiry && visibleInquiries.length > 0 && !inquiryId) {
      loadInquiryDetails(visibleInquiries[0].id);
    }
  }, [selectedInquiry, visibleInquiries, inquiryId, loadInquiryDetails]);

  const inquiryDetail = selectedInquiry && (
    <Box
      data-testid="partner-inquiry-detail-scroll"
      sx={{
        p: { xs: 2.5, md: '24px 28px' },
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {inquiryConverted ? (
        <Alert
          severity="success"
          data-testid="inquiry-converted-banner"
          sx={{ mb: 2, alignItems: 'center' }}
          action={convertedTripPath ? (
            <Button
              component={RouterLink}
              to={convertedTripPath}
              data-testid="inquiry-converted-trip-link"
              variant="outlined"
              color="success"
              size="small"
              endIcon={<ArrowForward />}
              sx={{ mr: 1, whiteSpace: 'nowrap' }}
            >
              {t('partnerLists.viewTrip')}
            </Button>
          ) : null}
        >
          {t('partnerLists.converted')}
          {convertedTripPath && selectedInquiry.linked_trip_title ? (
            <>
              {' · '}
              <Box
                component={RouterLink}
                to={convertedTripPath}
                data-testid="inquiry-converted-trip-title"
                sx={{ color: 'inherit', fontWeight: 600, textDecoration: 'underline' }}
              >
                {selectedInquiry.linked_trip_title}
              </Box>
            </>
          ) : null}
        </Alert>
      ) : null}

      <Box
        data-testid="partner-inquiry-detail-header"
        sx={{ mb: 2.5, pb: 2, borderBottom: `1px solid ${DOCUMENT.rule}` }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
          <Typography sx={{ ...documentEyebrowSx, mb: 0 }}>
            {t(`partnerLists.${getInquiryKind(selectedInquiry)}`)}
          </Typography>
          <InquirySubjectTag inquiry={selectedInquiry} t={t} />
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Typography
            sx={{
              fontFamily: DOCUMENT.display,
              fontSize: { xs: 22, md: 26 },
              fontWeight: 400,
              lineHeight: 1.15,
            }}
          >
            {selectedInquiry.user_name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography
              sx={{
                fontSize: 12,
                color: inquiryNeedsReply(selectedInquiry) ? DOCUMENT.accent : DOCUMENT.ink3,
              }}
            >
              {inquiryNeedsReply(selectedInquiry)
                ? t('partnerLists.needsReply')
                : t('partnerLists.answered')}
            </Typography>
            {selectedInquiry.booking_id ? (
              <Box
                component="button"
                type="button"
                sx={{
                  ...partnerLinkSx,
                  fontSize: 10,
                  opacity: detailsLoading ? 0.5 : 1,
                  pointerEvents: detailsLoading ? 'none' : 'auto',
                }}
                onClick={() => openBookingDetails(selectedInquiry.booking_id)}
              >
                {detailsLoading ? t('partnerLists.loadingShort') : t('partnerLists.viewDetails')}
              </Box>
            ) : null}
          </Box>
        </Box>

        <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2, mt: 0.6, lineHeight: 1.4 }}>
          {[
            selectedInquiry.user_email,
            selectedInquiry.created_at ? formatTime(selectedInquiry.created_at) : null,
            selectedInquiry.booking_reference
              ? t('partnerLists.bookingNumber', { ref: selectedInquiry.booking_reference })
              : null,
          ].filter(Boolean).join(' · ')}
        </Typography>

        <Box
          data-testid="partner-inquiry-trip-context"
          sx={{
            mt: 1.75,
            px: 1.75,
            py: 1.5,
            border: `1px solid ${DOCUMENT.ruleHard}`,
            backgroundColor: 'rgba(0,0,0,0.015)',
          }}
        >
          <Typography
            sx={{
              fontSize: 10,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: DOCUMENT.ink3,
              mb: 0.6,
            }}
          >
            {t('partnerLists.tripContext')}
          </Typography>
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: DOCUMENT.ink, lineHeight: 1.35 }}>
            {getInquiryDisplayTitle(selectedInquiry, t('partnerLists.customRequest'))}
          </Typography>
          <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2, mt: 0.5, lineHeight: 1.45 }}>
            {[
              selectedInquiry.destination,
              selectedInquiry.preferred_dates
                || [selectedInquiry.preferred_travel_date, selectedInquiry.preferred_end_date]
                  .filter(Boolean)
                  .join(' – ')
                || null,
              (() => {
                const travelers = Number(selectedInquiry.num_travelers || selectedInquiry.travelers || 0);
                if (!travelers) return null;
                return t('partnerLists.travelerCount', { count: travelers });
              })(),
              selectedInquiry.original_source || selectedInquiry.attribution || null,
              selectedInquiry.requested_team_member_name || t('partnerLists.anyoneAtAgency'),
              selectedInquiry.selected_rate_name
                ? `${selectedInquiry.selected_rate_name}${selectedInquiry.selected_rate_price ? ` · ${formatMoney(selectedInquiry.selected_rate_price)}` : ''}`
                : (selectedInquiry.base_price != null && selectedInquiry.base_price !== ''
                  ? formatMoney(selectedInquiry.base_price)
                  : null),
            ].filter(Boolean).join(' · ')}
          </Typography>
        </Box>

        {(selectedInquiry.dmc_id || selectedInquiry.dmc_company_name) ? (
          <Box
            data-testid="partner-inquiry-dmc"
            sx={{
              mt: 1.75,
              px: 1.75,
              py: 1.5,
              border: `1px solid ${DOCUMENT.ruleHard}`,
            }}
          >
            <Typography sx={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: DOCUMENT.ink3, mb: 0.6 }}>
              {t('partnerLists.dmcContact')}
            </Typography>
            <Typography sx={{ fontSize: 15, color: DOCUMENT.ink }}>
              {selectedInquiry.dmc_company_name || t('partnerLists.dmcContact')}
            </Typography>
            {selectedInquiry.dmc_contact_name ? (
              <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2, mt: 0.4 }}>
                {selectedInquiry.dmc_contact_name}
              </Typography>
            ) : null}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.6 }}>
              {selectedInquiry.dmc_email ? (
                <Box component="a" href={`mailto:${selectedInquiry.dmc_email}`} sx={partnerLinkSx}>
                  {selectedInquiry.dmc_email}
                </Box>
              ) : null}
              {selectedInquiry.dmc_phone ? (
                <Box component="a" href={`tel:${selectedInquiry.dmc_phone}`} sx={partnerLinkSx}>
                  {selectedInquiry.dmc_phone}
                </Box>
              ) : null}
            </Box>
            {selectedInquiry.dmc_notes ? (
              <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2, mt: 0.75, whiteSpace: 'pre-wrap' }}>
                {selectedInquiry.dmc_notes}
              </Typography>
            ) : null}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 1.5 }}>
              <TextField
                label={t('partnerLists.dmcNet')}
                value={dmcNet}
                onChange={(event) => setDmcNet(event.target.value)}
                variant="standard"
                inputProps={{ 'data-testid': 'partner-dmc-net', inputMode: 'decimal' }}
              />
              <TextField
                label={t('partnerLists.clientPrice')}
                value={clientPrice}
                onChange={(event) => setClientPrice(event.target.value)}
                variant="standard"
                inputProps={{ 'data-testid': 'partner-client-price', inputMode: 'decimal' }}
              />
            </Box>
            {(() => {
              const netNum = Number(dmcNet);
              const sellNum = Number(clientPrice);
              if (dmcNet === '' || clientPrice === '' || !Number.isFinite(netNum) || !Number.isFinite(sellNum)) {
                return null;
              }
              const margin = Math.round((sellNum - netNum) * 100) / 100;
              const percent = sellNum > 0 ? Math.round((margin / sellNum) * 1000) / 10 : null;
              return (
                <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2, mt: 1 }} data-testid="partner-dmc-margin">
                  {t('partnerLists.margin')}: {formatMoney(margin)}
                  {percent != null ? ` · ${t('partnerLists.marginPercent', { percent })}` : ''}
                </Typography>
              );
            })()}
            <Box
              component="button"
              type="button"
              disabled={savingQuote}
              data-testid="partner-save-quote"
              onClick={handleSaveQuote}
              sx={{ ...partnerOxbloodBtnSx, mt: 1.5 }}
            >
              {t('partnerLists.saveQuote')}
            </Box>
          </Box>
        ) : null}
      </Box>

      <Box sx={{ pr: 0.5 }}>
        {messagesLoading ? (
          <PartnerLoading />
        ) : (
          <PartnerInquiryMessageThread
            messages={visibleMessages}
            customerName={selectedInquiry.user_name}
          />
        )}
      </Box>

      <Box
        sx={{
          mt: 3,
          pt: 2.25,
          borderTop: `1px solid ${DOCUMENT.rule}`,
        }}
      >        <Typography sx={{ fontSize: 10, letterSpacing: '0.19em', textTransform: 'uppercase', color: DOCUMENT.ink3, display: 'block', mb: 1.1 }}>
          {t('partnerLists.reply')}
        </Typography>
        <Box
          component="textarea"
          value={replyMessage}
          onChange={(event) => setReplyMessage(event.target.value)}
          placeholder={t('partnerLists.replyPlaceholder')}
          sx={{
            width: '100%',
            background: 'transparent',
            border: `1px solid ${DOCUMENT.ruleHard}`,
            font: 'inherit',
            fontSize: 14.5,
            lineHeight: 1.65,
            p: '12px 14px',
            minHeight: 96,
            resize: 'none',
            color: DOCUMENT.ink,
            outline: 0,
            '&:focus': { borderColor: DOCUMENT.accent },
            '&::placeholder': { color: DOCUMENT.ink3 },
          }}
        />
        <Box sx={{ display: 'flex', gap: 2.25, alignItems: 'center', mt: 1.75, flexWrap: 'wrap' }}>
          {selectedInquiry.package_id && (
            <>
              <Box
                component="button"
                sx={partnerLinkSx}
                onClick={() => navigate(`/partner/packages/${selectedInquiry.package_id}/edit`)}
              >
                {t('partnerLists.attachPackage')}
              </Box>
              <Box
                component="button"
                sx={partnerLinkSx}
                onClick={() => {
                  setError(null);
                  setBookForClientOpen(true);
                }}
              >
                {t('partnerLists.clientLink')}
              </Box>
            </>
          )}
          <Box
            component="button"
            sx={partnerLinkSx}
            onClick={() => {
              if (!selectedInquiry) return;
              const params = new URLSearchParams();
              if (selectedInquiry.user_email) {
                params.set('clientEmail', selectedInquiry.user_email);
              }
              if (selectedInquiry.id) {
                params.set('inquiryId', String(selectedInquiry.id));
              }
              const title = getInquiryDisplayTitle(selectedInquiry, t('partnerLists.customRequest'));
              if (title) params.set('title', title);
              if (selectedInquiry.destination) {
                params.set('destination', selectedInquiry.destination);
              }
              if (selectedInquiry.preferred_travel_date) {
                params.set('startDate', selectedInquiry.preferred_travel_date);
              }
              if (selectedInquiry.preferred_end_date) {
                params.set('endDate', selectedInquiry.preferred_end_date);
              }
              if (selectedInquiry.package_id) {
                params.set('fromPackage', String(selectedInquiry.package_id));
              }
              navigate(`/trips/create?${params.toString()}`);
            }}
          >
            {t('partnerLists.createFromInquiry')}
          </Box>
          {!inquiryConverted && !inquiryClosed ? (
            <Box component="button" sx={partnerLinkSx} onClick={handleCreateProposal}>
              {t('partnerLists.createProposal')}
            </Box>
          ) : null}
          {!inquiryConverted && !inquiryClosed ? (
            <Box component="button" sx={partnerLinkSx} onClick={openMarkAsBookedDialog}>
              {t('partnerLists.markBooked')}
            </Box>
          ) : null}
          {!inquiryClosed && !inquiryConverted ? (
            <Box component="button" sx={partnerLinkSx} onClick={handleCloseInquiry}>
              {t('partnerLists.closeInquiry')}
            </Box>
          ) : null}
          <Box
            component="button"
            type="button"
            sx={{
              ...partnerLinkSx,
              ...(sending ? { opacity: 0.5, pointerEvents: 'none' } : null),
            }}
            onClick={() => handleArchiveInquiry(!selectedInquiry?.partner_archived)}
          >
            {selectedInquiry?.partner_archived
              ? t('partnerLists.restoreInquiry')
              : t('partnerLists.archiveInquiry')}
          </Box>
          <Button
            sx={{ ...partnerOxbloodBtnSx, ml: 'auto' }}
            onClick={sendReply}
            disabled={!replyMessage.trim() || sending}
          >
            {sending ? <CircularProgress size={16} sx={{ color: DOCUMENT.ivory }} /> : t('partnerLists.sendReply')}
          </Button>
        </Box>
      </Box>
    </Box>
  );

  return (
    <PartnerPageShell
      fullPage
      error={error}
      onClearError={() => setError(null)}
      success={successMessage}
      onClearSuccess={() => setSuccessMessage(null)}
    >
      <PartnerPageHeader
        compact
        title={t('partnerLists.inquiriesTitle')}
        actions={avgHours != null ? (
          <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, fontFamily: DOCUMENT.sans }}>
            {t('partnerLists.avgReply', { time: `${avgHours}h` })}
          </Typography>
        ) : null}
      />

      <PartnerFilterBar
        chips={[
          { id: 'all', label: t('partnerLists.filterAll'), count: counts.all },
          { id: 'needsReply', label: t('partnerLists.filterNeedsReply'), count: counts.needsReply },
          { id: 'answered', label: t('partnerLists.filterAnswered'), count: counts.answered },
          { id: 'archived', label: t('partnerLists.filterArchived'), count: counts.archived },
        ]}
        activeChip={activeTab}
        onChipChange={setActiveTab}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('partnerLists.searchInquiries')}
      />

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', py: 1.5, flexShrink: 0 }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="partner-inquiry-team-filter-label">
            {t('partnerLists.teamMemberFilter')}
          </InputLabel>
          <Select
            labelId="partner-inquiry-team-filter-label"
            label={t('partnerLists.teamMemberFilter')}
            value={teamMemberFilter}
            onChange={(e) => setTeamMemberFilter(e.target.value)}
            data-testid="partner-inquiry-team-filter"
          >
            <MenuItem value="all">{t('partnerLists.teamFilterAll')}</MenuItem>
            {partnerSession.isPartnerUser ? (
              <MenuItem value="mine">{t('partnerLists.teamFilterMine')}</MenuItem>
            ) : null}
            <MenuItem value="anyone">{t('partnerLists.teamFilterAnyone')}</MenuItem>
            {teamMembers.map((member) => (
              <MenuItem key={member.id} value={String(member.id)}>
                {member.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <PartnerLoading />
        </Box>
      ) : visibleInquiries.length === 0 ? (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <PartnerEmptyState
            title={search || activeTab !== 'all' ? t('partnerLists.noResults') : t('partnerLists.noInquiries')}
            body={search || activeTab !== 'all' ? t('partnerLists.noResultsBody') : t('partnerLists.noInquiriesBody')}
          />
        </Box>
      ) : (
        <Box
          data-testid="partner-inquiries-workspace"
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            border: `1px solid ${DOCUMENT.ruleHard}`,
            mt: 2,
            mb: 0,
            flex: 1,
            minHeight: 0,
            bgcolor: DOCUMENT.surface,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              width: { md: '34%' },
              maxWidth: { md: 420 },
              flex: { xs: '0 0 auto', md: '0 0 34%' },
              alignSelf: 'stretch',
              borderRight: { md: `1px solid ${DOCUMENT.ruleHard}` },
              borderBottom: { xs: `1px solid ${DOCUMENT.ruleHard}`, md: 0 },
              overflowY: 'auto',
              minHeight: 0,
              maxHeight: { xs: '32%', md: 'none' },
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {visibleInquiries.map((inquiry) => {
              const current = selectedInquiry?.id === inquiry.id;
              const needsReply = inquiryNeedsReply(inquiry);
              const hasUnread = Number(inquiry.unread_count || 0) > 0;
              const displayTitle = getInquiryDisplayTitle(inquiry, t('myInquiries.customTripRequest'));
              const displaySubtitle = getInquiryDisplaySubtitle(inquiry, displayTitle);
              const contextLine = [
                inquiry.booking_reference,
                displayTitle,
                displaySubtitle,
              ].filter(Boolean).join(' · ');
              const assigneeLabel = inquiry.requested_team_member_name
                ? t('partnerLists.forTeamMember', { name: inquiry.requested_team_member_name })
                : t('partnerLists.anyoneAtAgency');
              const statusLabel = needsReply
                ? `${t(`partnerLists.${getInquiryKind(inquiry)}`)} · ${t('partnerLists.needsReply')}`
                : t('partnerLists.answered');
              const stripColor = hasUnread
                ? ALERT
                : (current ? DOCUMENT.accent : null);
              return (
                <Box
                  key={inquiry.id}
                  component="button"
                  type="button"
                  aria-current={current}
                  data-testid="partner-inquiry-row"
                  data-unread={hasUnread ? 'true' : 'false'}
                  onClick={() => loadInquiryDetails(inquiry.id)}
                  sx={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    background: current ? '#F2ECE1' : 'none',
                    boxShadow: stripColor ? `inset 2px 0 0 ${stripColor}` : 'none',
                    border: 0,
                    borderBottom: `1px solid ${DOCUMENT.rule}`,
                    font: 'inherit',
                    color: 'inherit',
                    px: 2,
                    py: 1.15,
                    cursor: 'pointer',
                    '&:hover': { background: '#F5F0E6' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: 13.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                      }}
                    >
                      {inquiry.user_name}
                    </Typography>
                    <InquirySubjectTag inquiry={inquiry} t={t} />
                    <Typography sx={{ ml: 'auto', flexShrink: 0, fontSize: 11, color: DOCUMENT.ink3, fontVariantNumeric: 'tabular-nums' }}>
                      {formatTime(inquiry.last_message_at || inquiry.created_at)}
                    </Typography>
                  </Box>
                  {contextLine ? (
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: DOCUMENT.ink2,
                        mt: 0.35,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {contextLine}
                    </Typography>
                  ) : null}
                  <Typography
                    sx={{
                      fontSize: 11.5,
                      mt: 0.3,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: needsReply ? ALERT : DOCUMENT.ink3,
                    }}
                  >
                    <Box component="span" sx={{ color: DOCUMENT.ink3 }}>
                      {assigneeLabel}
                    </Box>
                    {' · '}
                    {statusLabel}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              alignSelf: 'stretch',
            }}
          >
            {selectedInquiry ? inquiryDetail : (
              <PartnerEmptyState
                title={t('partnerLists.selectInquiry')}
                body={t('partnerLists.selectInquiryBody')}
              />
            )}
          </Box>
        </Box>
      )}

      <Dialog open={markAsBookedOpen} onClose={() => setMarkAsBookedOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle color="success" />
            {t('partnerLists.markBooked')}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {inquiryIsOwnClient
              ? t('partnerLists.markBookedHelpOwnClient')
              : t('partnerLists.markBookedHelp', { rate: bookedFeeRate, ...introductionFeeCopyVars() })}
          </Typography>

          <NumericTextField
            fullWidth
            label={t('partnerLists.bookingAmount')}
            value={bookingAmount}
            onValueChange={setBookingAmount}
            allowDecimal
            allowEmpty
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>
            }}
            sx={{ mb: 2 }}
            required
          />

          {/* The preview has to agree with what the server will charge: tier
              rate on marketplace inquiries, 0% on a private client link. */}
          {bookingAmount !== '' && bookingAmount != null && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('partnerLists.commissionPreview', {
                rate: bookedFeeRate,
                amount: computeIntroductionFeeAmount(bookingAmount, bookedFeeRate).toFixed(2),
                ...introductionFeeCopyVars()
              })}
            </Alert>
          )}

          <TextField
            fullWidth
            label={t('partnerLists.customerName')}
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label={t('partnerLists.travelDate')}
            type="date"
            value={travelDate}
            onChange={(e) => setTravelDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label={t('partnerLists.notesOptional')}
            value={bookingNotes}
            onChange={(e) => setBookingNotes(e.target.value)}
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkAsBookedOpen(false)}>{t('partnerLists.cancel')}</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleMarkAsBooked}
            disabled={!bookingAmount || markingAsBooked}
            startIcon={markingAsBooked ? <CircularProgress size={20} /> : <CheckCircle />}
          >
            {t('partnerLists.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <PartnerBookingDetailsDialog
        open={detailsModal}
        booking={detailsBooking}
        onClose={closeBookingDetails}
        hideMessage
        onConfirm={(booking) => {
          closeBookingDetails();
          navigate('/partner/bookings', { state: { openBookingId: booking.id } });
        }}
      />
      <BookForClientDialog
        open={bookForClientOpen}
        onClose={() => setBookForClientOpen(false)}
        packageId={selectedInquiry?.package_id}
        packageTitle={selectedInquiry?.package_title}
        currency={selectedInquiry?.currency || 'USD'}
        pricePerPerson={selectedInquiry?.base_price || 0}
        defaultEmail={selectedInquiry?.user_email || ''}
        defaultTravelers={selectedInquiry?.num_travelers || 1}
        onSent={(result) => {
          setSuccessMessage(t('bookForClient.sent', {
            email: result.email,
            ref: result.bookingReference,
          }));
        }}
      />
    </PartnerPageShell>
  );
};

export default PartnerInquiries;
